import { message } from "antd";
import i18n from "src/i18n/config";
import {
  ChatbotQueryLimitReachedError,
  fetchChatSuggestions,
  getActiveChatSessionId,
  sendImageMessageToAPI,
  sendMessageToAPI,
  storeChat,
} from "src/services/ChatbotAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { getAudienceListDisplayName } from "src/utils/audienceListLabels";
import { translateCampaignLabel } from "src/utils/campaignLabels";
import { handleChatFallbacks } from "src/utils/chatFallbacks";
import { createTriggers, menuItems } from "src/utils/constants";
import { getTime } from "../layouts/AskDatatram/ChatHandler";
import type { Message } from "../layouts/AskDatatram/ChatInterface";
import type { CampaignChannel } from "../layouts/Engagements/EngagementInterfaces";
import {
  CHANNEL_REPLY_OPTIONS,
  isChannelQuestionReply,
} from "../layouts/AskDatatram/CampaignWizard/ChatCampaignChoiceConstants";

const t = (key: string, options?: Record<string, unknown>) =>
  i18n.t(`chatScreen.${key}`, options);

const ACTION_DISPLAY_KEYS: Record<string, string> = {
  "Create New Campaign": "createNewCampaign",
  "Create New List": "quickActionCreateListTitle",
  "Show campaign performance": "showCampaignPerformance",
  "View reports": "viewReports",
  Yes: "yes",
  Edit: "edit",
  "Send Now": "sendNow",
  Schedule: "schedule",
};

const CAMPAIGN_KEYWORDS = ["campaign", "create", "حملة", "إنشاء", "انشاء"];
const CREATE_CAMPAIGN_STEPS = [
  "impressionsStep",
  "conversionsStep",
  "roasStep",
  "engagementStep",
];
const SUGGESTIONS_DELAY_MS = 8000;
const NO_RESPONSE_TEXT = "No response text found.";

const MIME_TYPES_BY_EXTENSION: Record<string, string> = {
  csv: "text/csv",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  pdf: "application/pdf",
  png: "image/png",
  webp: "image/webp",
};

const isCampaignIntent = (intent: string) => intent.endsWith("_campaign");

const isCampaignKeyword = (value: string) =>
  CAMPAIGN_KEYWORDS.includes(value.trim().toLowerCase());

const isCreateCampaignStep = (step: ConversationStep) =>
  CREATE_CAMPAIGN_STEPS.includes(step ?? "");

const getDisplayText = (rawText: string) => {
  const key = ACTION_DISPLAY_KEYS[rawText];
  if (key) {return t(key);}
  // Audience lists travel through the pipeline as their canonical English name,
  // so the label has to be looked up before echoing the choice back to the user.
  const audienceLabel = getAudienceListDisplayName(rawText);
  if (audienceLabel !== rawText) {return audienceLabel;}
  return translateCampaignLabel(rawText);
};

export type ConversationStep =
  | "impressionsStep"
  | "roasStep"
  | "conversionsStep"
  | "engagementStep"
  | "askAudience"
  | "askWebsite"
  | "confirmCampaign"
  | null;

interface FilePayload {
  name: string;
  type: string;
  dataUrl: string;
}

let sessionInitPromise: Promise<number> | null = null;

interface MessageHandlerParams {
  textToSend: string;
  conversationStep: ConversationStep;
  input: string;
  selectedFile: File | File[] | null;
  previewURL: string | null;
  campaignName: string;
  user_id: string | null;
  tenant_id: string;
  clearedRef: React.MutableRefObject<boolean>;
  setQuery: React.Dispatch<React.SetStateAction<Message[]>>;
  setInput: (_value: string) => void;
  setConversationStep: (_step: ConversationStep) => void;
  setLoading: (_loading: boolean) => void;
  setIsFetching: (_fetching: boolean) => void;
  setFirstMessageSent: (_sent: boolean) => void;
  setSuggestedQuestions: (_questions: string[]) => void;
  onQueryLimitReached?: (_backendDetail: string) => void;
}

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const notifySaveFailure = () => {
  message.error("Unable to save chat history right now.");
};

export const createMessageHandler = (params: MessageHandlerParams) => {
  const {
    textToSend,
    conversationStep,
    input,
    selectedFile,
    previewURL,
    campaignName,
    user_id,
    tenant_id,
    clearedRef,
    setQuery,
    setInput,
    setConversationStep,
    setLoading,
    setIsFetching,
    setFirstMessageSent,
    setSuggestedQuestions,
    onQueryLimitReached,
  } = params;

  const handleQueryLimitError = (error: unknown): boolean => {
    if (!(error instanceof ChatbotQueryLimitReachedError)) {
      return false;
    }
    onQueryLimitReached?.(error.backendDetail);
    return true;
  };

  const buildFilePayload = async (): Promise<FilePayload | null> => {
    const file = Array.isArray(selectedFile) ? selectedFile[0] : selectedFile;
    const ext = file?.name.split(".").pop()?.toLowerCase();
    const isSupportedFile =
      !!file &&
      (file.type.startsWith("image/") ||
        ["png", "jpg", "jpeg", "pdf", "csv"].includes(ext || "") ||
        ["application/pdf", "text/csv", "application/csv", "application/vnd.ms-excel"].includes(file.type));
    if (!isSupportedFile) {return null;}

    try {
      const dataUrl = await readFileAsDataUrl(file);
      if (!dataUrl) {return null;}
      const fileType =
        file.type ||
        (ext ? MIME_TYPES_BY_EXTENSION[ext] : undefined) ||
        "application/octet-stream";
      return { name: file.name, type: fileType, dataUrl };
    } catch {
      return null;
    }
  };

  const persistChat = async (
    messageText: string,
    responseText: string,
    sessionHeadlineOverride?: string,
    filePayload?: FilePayload | null
  ) => {
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage && !filePayload) {return;}
    if (!responseText.trim()) {return;}

    const resolvedFilePayload =
      filePayload === undefined ? await buildFilePayload() : filePayload;
    const savedUserId = Number(localStorage.getItem("user_id")) || 621;
    const currentSessionId = Number(getActiveChatSessionId()) || 0;
    const sessionHeadline =
      sessionHeadlineOverride?.trim() || campaignName?.trim() || "AI Chat Session";

    const saveChat = (sessionId: number) =>
      storeChat({
        user_id: savedUserId,
        session_id: sessionId,
        message: messageText,
        response: responseText,
        session_headline: sessionHeadline,
        lang: getApiLangFromI18n(i18n),
        file_data: resolvedFilePayload?.dataUrl,
        file_name: resolvedFilePayload?.name,
        file_type: resolvedFilePayload?.type,
      });

    if (currentSessionId !== 0) {
      saveChat(currentSessionId).catch(notifySaveFailure);
      return;
    }

    if (sessionInitPromise) {
      sessionInitPromise
        .then(sessionId => (sessionId ? saveChat(sessionId) : undefined))
        .catch(notifySaveFailure);
      return;
    }

    sessionInitPromise = saveChat(0)
      .then((res: { session_id?: number | string } | undefined) => {
        const newSessionId = Number(res?.session_id) || 0;
        if (newSessionId) {
          localStorage.setItem("session_id", String(newSessionId));
        }
        return newSessionId;
      })
      .catch(() => {
        notifySaveFailure();
        return 0;
      })
      .finally(() => {
        sessionInitPromise = null;
      });
  };

  const appendBotReply = (responseText: string, campaignChannelOptions?: CampaignChannel[]) => {
    const reply: Message = {
      sender: "bot",
      text: responseText,
      type: "botMessage",
      timestamp: getTime(),
      campaignChannelOptions,
    };
    setQuery(prev => (clearedRef.current ? prev : [...prev, reply]));
  };

  const appendErrorReply = () => {
    message.error(" Error in handleSendMessage API call:");
    setQuery(prev => [
      ...prev,
      {
        sender: "bot",
        text: t("somethingWentWrongTryAgain"),
        timestamp: getTime(),
        type: "botMessage",
      },
    ]);
  };

  const scheduleSuggestions = (messageId: string | undefined) => {
    if (!messageId) {return;}
    setTimeout(async () => {
      const suggestions = await fetchChatSuggestions(messageId);
      if (!clearedRef.current && suggestions.length > 0) {
        setSuggestedQuestions(suggestions);
      }
    }, SUGGESTIONS_DELAY_MS);
  };

  const askToConfirmCampaign = (leadingMessages: Message[], textToProcess: string, filePayload: FilePayload | null) => {
    const botMessage: Message = {
      sender: "bot",
      text: t("confirmCreateCampaignQuestion"),
      timestamp: getTime(),
      type: "confirmCampaign",
      options: ["Yes", "No"],
    };
    setQuery(prev => [...prev, ...leadingMessages, botMessage]);
    setConversationStep("confirmCampaign");
    setInput("");
    persistChat(getDisplayText(textToProcess), botMessage.text || "", undefined, filePayload);
  };

  return async (messageText?: string) => {
    const textToProcess = messageText ?? textToSend ?? "";
    const filePayload = await buildFilePayload();
    const hasFilePayload = !!filePayload;
    if (!textToProcess.trim() && !hasFilePayload) {return;}

    const fallbackResponse = textToProcess.trim()
      ? handleChatFallbacks(textToProcess)
      : null;
    if (fallbackResponse) {
      const fallbackText = fallbackResponse
        .map(item => item.text)
        .filter(Boolean)
        .join("\n");
      const userMessage: Message = {
        sender: "user",
        text: textToProcess,
        timestamp: getTime(),
        type: "userMessage",
      };
      setQuery(prev => [...prev, userMessage, ...fallbackResponse]);
      setInput("");
      persistChat(getDisplayText(textToProcess), fallbackText, undefined, filePayload);
      return;
    }

    if (input && isCampaignKeyword(input) && !isCreateCampaignStep(conversationStep)) {
      askToConfirmCampaign([], textToProcess, filePayload);
      return;
    }

    const userMessage: Message = {
      sender: "user",
      text: getDisplayText(textToProcess),
      timestamp: getTime(),
      ...(selectedFile && previewURL && {
        file: {
          name: Array.isArray(selectedFile) ? selectedFile[0].name : selectedFile.name,
          type: Array.isArray(selectedFile) ? selectedFile[0].type : selectedFile.type,
          url: previewURL,
        },
      }),
    };
    setQuery(prev => [...prev, userMessage]);
    setInput("");

    if (!textToProcess.trim() && filePayload) {
      setLoading(true);
      setIsFetching(true);
      try {
        const { botMessage, chatName, messageId } = await sendImageMessageToAPI(
          textToProcess,
          user_id,
          tenant_id,
          filePayload,
          getTime,
          getApiLangFromI18n(i18n)
        );

        if (clearedRef.current) {return;}

        const responseText = botMessage?.text?.trim() || NO_RESPONSE_TEXT;
        appendBotReply(responseText);
        persistChat(getDisplayText(textToProcess), responseText, chatName || undefined, filePayload);
        scheduleSuggestions(messageId);
        return responseText;
      } catch (error) {
        if (clearedRef.current) {return;}
        if (handleQueryLimitError(error)) {return;}
        appendErrorReply();
      } finally {
        setLoading(false);
        setIsFetching(false);
      }
      return;
    }

    const menu = menuItems.find(m => m.text === textToProcess);
    if (menu) {
      const botMessage: Message = {
        sender: "bot",
        timestamp: getTime(),
        type: "menuQuestions",
        menu,
      };

      setQuery(prev => {
        const updated = [...prev];
        if (
          updated.length > 0 &&
          (updated[updated.length - 1].type === "menuQuestions" ||
            updated[updated.length - 1].type === "campaignOptions")
        ) {
          updated.pop();
        }
        return [...updated, botMessage];
      });
      setSuggestedQuestions([]);
      persistChat(textToProcess, t("relatedQuestions"), undefined, filePayload);
      return;
    }

    const normalizedText = textToProcess.trim().toLowerCase();
    if (createTriggers.some(trigger => normalizedText.includes(trigger))) {
      const userMsg: Message = {
        sender: "user",
        timestamp: getTime(),
        type: "userMessage",
      };
      const wizardMessage: Message = {
        sender: "bot",
        timestamp: getTime(),
        type: "campaignWizard",
      };
      setQuery(prev => [...prev, userMsg, wizardMessage]);
      setConversationStep(null);
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      persistChat(getDisplayText(textToProcess), t("createCampaignReply"), undefined, filePayload);
      return;
    }

    if (isCampaignKeyword(normalizedText) && !isCreateCampaignStep(conversationStep)) {
      const userMsg: Message = {
        sender: "user",
        text: textToProcess,
        timestamp: getTime(),
        type: "userMessage",
      };
      askToConfirmCampaign([userMsg], textToProcess, filePayload);
      return;
    }

    setLoading(true);
    setIsFetching(true);
    try {
      const { botMessage, chatName, messageId, intent, campaignChannel } = filePayload
        ? await sendImageMessageToAPI(
            textToProcess,
            user_id,
            tenant_id,
            filePayload,
            getTime,
            getApiLangFromI18n(i18n)
          )
        : await sendMessageToAPI(textToProcess, user_id, tenant_id, getTime, getApiLangFromI18n(i18n));

      if (clearedRef.current) {return;}

      // Intent-driven UI: the backend classifies the message and returns an
      // `intent`. When it signals campaign creation ("engagement"), suppress the
      // text reply and launch the campaign wizard UI instead of showing it.
      if (intent?.includes("engagement") || (campaignChannel && intent?.some(isCampaignIntent))) {
        const wizardMessage: Message = {
          sender: "bot",
          timestamp: getTime(),
          type: "campaignWizard",
          campaignChannel,
        };
        setQuery(prev => (clearedRef.current ? prev : [...prev, wizardMessage]));
        setConversationStep(null);
        setFirstMessageSent(true);
        setSuggestedQuestions([]);
        persistChat(getDisplayText(textToProcess), t("createCampaignReply"), chatName || undefined, filePayload);
        return t("createCampaignReply");
      }

      const responseText = botMessage?.text?.trim() || NO_RESPONSE_TEXT;
      const isChannelQuestion = !campaignChannel && isChannelQuestionReply(responseText);
      appendBotReply(responseText, isChannelQuestion ? CHANNEL_REPLY_OPTIONS : undefined);
      persistChat(getDisplayText(textToProcess), responseText, chatName || undefined, filePayload);

      if (isCreateCampaignStep(conversationStep)) {
        setConversationStep(null);
      }

      scheduleSuggestions(messageId);
      return responseText;
    } catch (error) {
      if (clearedRef.current) {return;}
      if (handleQueryLimitError(error)) {return;}
      appendErrorReply();
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };
};
